import React from 'react';
import { Container, Row, Col, Card, Button, Badge } from 'react-bootstrap';

export default function CrewCoinPricingPage() {
  const [checkoutLoading, setCheckoutLoading] = React.useState('');
  const [showPromoModal, setShowPromoModal] = React.useState(false);
  const [copied, setCopied] = React.useState(false);

  React.useEffect(() => {
    const timer = setTimeout(() => {
      setShowPromoModal(true);
    }, 1500);

    return () => clearTimeout(timer);
  }, []);

  const closePromoModal = () => {
    setShowPromoModal(false);
  };

  const copyPromoCode = async () => {
    try {
      await navigator.clipboard.writeText('MONTH1FREE');
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch (e) {
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    }
  };

  const startStripeCheckout = async (plan, billingInterval) => {
    try {
      setCheckoutLoading(`${plan}:${billingInterval}`);
      const res = await fetch('https://crewcoin.herokuapp.com/billing/create-checkout-session-public', {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ plan, billingInterval }),
      });

      const data = await res.json();
      if (data?.checkoutUrl) {
        window.location.href = data.checkoutUrl;
        return;
      }

      alert(data?.message || 'Unable to start checkout right now.');
    } catch (err) {
      alert('Unable to start checkout right now.');
    } finally {
      setCheckoutLoading('');
    }
  };

  const plans = [
    {
      key: 'team',
      name: 'Team',
      price: '$9',
      period: '/month',
      description: 'Great for smaller crews just getting started with Crew Coin.',
      features: [
        'Up to 30 users (employees)',
        '1 admin account',
        'Core platform features',
        'Email support',
      ],
      cta: 'Start Team',
      featured: false,
    },
    {
      key: 'business',
      name: 'Business',
      price: '$19',
      period: '/month',
      description: 'Perfect for growing restaurants, retail stores, and operations.',
      features: [
        'Up to 50 users (employees)',
        'Up to 3 admin accounts',
        'Everything in Team',
        'Basic analytics',
        'Priority support',
      ],
      cta: 'Go Business',
      featured: true,
    },
    {
      key: 'enterprise',
      name: 'Enterprise',
      price: '$59',
      period: '/month',
      description: 'For large teams and multi-location operations.',
      features: [
        'Up to 200 users (employees)',
        '20 admin accounts',
        'Everything in Business',
        'Advanced analytics',
        'Dedicated onboarding',
      ],
      cta: 'Choose Enterprise',
      featured: false,
    },
  ];

  const styles = {
    page: {
      minHeight: '100vh',
      background: '#f8fafc',
      padding: '42px 0 56px',
      color: '#1f2937',
    },
    heroWrap: {
      width: '100%',
      maxWidth: 920,
      margin: '0 auto',
      textAlign: 'center',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
    },
    logo: {
      height: 125,
      marginTop: -50,
      marginBottom: -20,
      width: 'auto',
      display: 'block',
      margin: '0 auto 1px',
    },
    title: {
      fontSize: '3.35rem',
      fontWeight: 800,
      letterSpacing: '-0.02em',
      marginBottom: 14,
      color: '#1f2937',
      textAlign: 'center',
      width: '100%',
    },
    subtitle: {
      fontSize: '1.56rem',
      color: '#4b5563',
      lineHeight: 1.6,
      marginBottom: 10,
      textAlign: 'center',
      width: '100%',
    },
    savings: {
      fontSize: '1.75rem',
      fontWeight: 700,
      color: '#b45309',
      marginBottom: 0,
      textAlign: 'center',
      width: '100%',
    },
    card: {
      borderRadius: 14,
      border: '2px solid #dbe3ec',
      boxShadow: '0 8px 18px rgba(15,23,42,.06)',
      overflow: 'hidden',
      backgroundColor: '#fff',
    },
    featuredCard: {
      border: '2px solid #f2b705',
      boxShadow: '0 0 0 3px rgba(242,183,5,.10), 0 10px 24px rgba(242,183,5,.16)',
    },
    cardBody: {
      padding: 24,
    },
    planTitle: {
      fontSize: '2.5rem',
      fontWeight: 800,
      marginBottom: 8,
      color: '#1f2937',
    },
    desc: {
      color: '#374151',
      fontSize: '1.4rem',
      lineHeight: 1.55,
      marginBottom: 14,
      fontWeight: 500,
    },
    priceLine: {
      display: 'flex',
      alignItems: 'flex-end',
      gap: 6,
      marginBottom: 4,
    },
    price: {
      fontSize: '3.7rem',
      lineHeight: 1,
      fontWeight: 800,
      color: '#111827',
    },
    period: {
      color: '#6b7280',
      fontSize: '1.18rem',
      marginBottom: 8,
    },
    annual: {
      fontSize: '1.12rem',
      color: '#b45309',
      fontWeight: 700,
      marginBottom: 14,
    },
    featureList: {
      listStyle: 'none',
      padding: 0,
      margin: 0,
    },
    featureItem: {
      display: 'flex',
      alignItems: 'flex-start',
      gap: 9,
      marginBottom: 10,
      color: '#374151',
      fontSize: '1.5rem',
      lineHeight: 1.55,
    },
    bullet: {
      width: 9,
      height: 9,
      borderRadius: 999,
      marginTop: 5,
      backgroundColor: '#f2b705',
      flexShrink: 0,
      boxShadow: '0 0 0 3px rgba(242,183,5,.16)',
    },
    adminNote: {
      marginTop: 8,
      fontSize: '1.08rem',
      color: '#374151',
      lineHeight: 1.5,
      fontStyle: 'italic',
    },
    btnPrimary: {
      borderRadius: 12,
      fontWeight: 800,
      fontSize: '1.5rem',
      padding: '13px 16px',
      backgroundColor: '#111827',
      borderColor: '#111827',
      boxShadow: '0 8px 16px rgba(17,24,39,.18)',
      marginTop: '10px',
      marginBottom: '10px',
    },
    btnFeatured: {
      borderRadius: 12,
      fontWeight: 800,
      fontSize: '1.5rem',
      padding: '13px 16px',
      background: 'linear-gradient(180deg, #f7c948 0%, #e8a400 100%)',
      borderColor: '#e8a400',
      color: '#fff',
      boxShadow: '0 12px 24px rgba(232,164,0,.34)',
      marginTop: '10px',
      marginBottom: '10px',
    },
    btnSecondary: {
      borderRadius: 12,
      fontWeight: 800,
      fontSize: '1.5rem',
      padding: '13px 16px',
      borderColor: '#cbd5e1',
      backgroundColor: '#fff',
      color: '#374151',
      marginBottom: '10px',
    },
    panel: {
      marginTop: 34,
      borderRadius: 14,
      border: '1px solid #f3e3b2',
      backgroundColor: '#fff9e8',
      padding: '18px 18px 16px',
    },
    panelTitle: {
      textAlign: 'center',
      fontSize: '2rem',
      fontWeight: 800,
      marginBottom: 10,
      color: '#1f2937',
    },
    panelText: {
      textAlign: 'center',
      color: '#4b5563',
      fontSize: '1.5rem',
      lineHeight: 1.5,
      marginBottom: 12,
    },
    statCard: {
      border: '1px solid #f3e3b2',
      borderRadius: 10,
      background: '#fff',
      padding: 12,
    },
    testimonial: {
      textAlign: 'center',
      fontSize: '1.04rem',
      color: '#4b5563',
      marginTop: 10,
      marginBottom: 0,
      fontStyle: 'italic',
    },
    promoCodePill: {
      fontWeight: 800,
      letterSpacing: '.08em',
      fontSize: 'clamp(1.05rem, 2.2vw, 1.2rem)',
      color: '#0f3f9e',
      background: '#f8fbff',
      border: '1px solid #cfe0ff',
      borderRadius: 8,
      padding: '9px 14px',
      display: 'inline-block',
      wordBreak: 'break-word',
      fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
    },
    promoBottomWrap: {
      margin: '18px auto 0',
      maxWidth: 680,
      width: '100%',
      border: '1px solid #e5e7eb',
      borderRadius: 12,
      background: '#ffffff',
      padding: '10px 12px',
      boxShadow: '0 4px 12px rgba(15,23,42,.06)',
    },
    promoBottomLabel: {
      fontSize: '.84rem',
      fontWeight: 700,
      letterSpacing: '.09em',
      color: '#6b7280',
      textTransform: 'uppercase',
      marginBottom: 8,
      textAlign: 'center',
    },
    promoBottomRow: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: 8,
      flexWrap: 'wrap',
    },
    promoBottomSegment: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      textAlign: 'center',
      minHeight: 36,
      flex: '1 1 160px',
      padding: '2px 8px',
      position: 'relative',
    },
    promoBottomText: {
      margin: 0,
      color: '#111827',
      fontSize: 'clamp(1.1rem, 2.2vw, 1.25rem)',
      lineHeight: 1.2,
      textAlign: 'center',
      fontWeight: 700,
      letterSpacing: '.02em',
    },
    promoBottomActions: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 8,
      flexWrap: 'wrap',
      flex: '1 1 160px',
      minHeight: 36,
    },
    promoBottomBtn: {
      borderRadius: 8,
      fontWeight: 700,
      padding: '6px 12px',
      borderColor: '#111827',
      background: '#111827',
      color: '#fff',
      boxShadow: '0 2px 6px rgba(17,24,39,.18)',
    },
    promoOverlay: {
      position: 'fixed',
      inset: 0,
      background: 'rgba(15, 23, 42, 0.18)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '16px',
      zIndex: 99999,
    },
    promoModalCard: {
      width: '100%',
      maxWidth: 420,
      background: '#ffffff',
      borderRadius: 16,
      border: '1px solid #e8edf5',
      boxShadow: '0 20px 44px rgba(15,23,42,.22)',
      overflow: 'hidden',
      fontFamily: '"Segoe UI Variable", "Inter", "SF Pro Text", "Aptos", "Helvetica Neue", Arial, sans-serif',
    },
    promoModalHeader: {
      padding: '12px 14px',
      borderBottom: '1px solid #edf2f7',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: 8,
      background: '#fcfdff',
    },
    promoModalTitle: {
      margin: 0,
      fontSize: '2rem',
      fontWeight: 800,
      letterSpacing: '-0.02em',
      color: '#0f172a',
      fontFamily: '"Segoe UI Variable Display", "Inter", "SF Pro Display", "Aptos Display", sans-serif',
    },
    promoModalBody: {
      padding: '14px',
      color: '#475569',
      fontSize: '1.08rem',
      lineHeight: 1.5,
    },
    promoModalFooter: {
      padding: '10px 14px 14px',
      display: 'flex',
      gap: 8,
      justifyContent: 'flex-end',
      flexWrap: 'wrap',
    },
  };

  return (
    <div style={styles.page}>
      <style>{`
        .cc-promo-overlay {
          animation: ccFadeIn 180ms ease-out;
        }
        .cc-promo-modal-card {
          animation: ccSlideDown 240ms cubic-bezier(.2,.8,.2,1);
          transform-origin: top center;
        }
        @keyframes ccFadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes ccSlideDown {
          from { opacity: 0; transform: translateY(-80px) scale(.98); }
          to { opacity: 1; transform: translateY(0) scale(1); }
        }
        .cc-promo-bottom-row > .cc-promo-bottom-segment:not(:first-child),
        .cc-promo-bottom-row > .cc-promo-bottom-actions {
          border-left: 1px solid #eef2f7;
        }
        @media (max-width: 576px) {
          .cc-promo-modal-card {
            max-width: 320px !important;
          }
          .cc-promo-modal-title {
            font-size: 1.7rem !important;
            line-height: 1.12 !important;
          }
          .cc-promo-modal-body {
            font-size: .9rem !important;
          }
          .cc-promo-bottom-row {
            flex-direction: column !important;
            align-items: stretch !important;
            justify-content: center !important;
            gap: 8px !important;
          }
          .cc-promo-bottom-segment,
          .cc-promo-bottom-actions {
            width: 100% !important;
            flex: 1 1 auto !important;
            justify-content: center !important;
            border-left: none !important;
          }
          .cc-promo-bottom-label {
            font-size: .95rem !important;
          }
          .cc-promo-bottom-text {
            font-size: 1.3rem !important;
          }
          .cc-promo-bottom-code {
            font-size: 1.25rem !important;
            padding: 10px 14px !important;
          }
        }
        @media (min-width: 992px) {
          .cc-promo-bottom {
            padding: 14px 16px !important;
          }
          .cc-promo-modal-card {
            max-width: 380px !important;
          }
          .cc-promo-modal-title {
            font-size: 1.6rem !important;
            line-height: 1.15 !important;
          }
          .cc-promo-modal-body {
            font-size: .98rem !important;
            line-height: 1.45 !important;
          }
        }
      `}</style>
      <Container>
        <Row className="justify-content-center">
          <Col lg={12} className="d-flex justify-content-center">
            <div style={styles.heroWrap}>
              <img src="https://crewcoin.app/img/crewcoinlogo.png" alt="CrewCoin" style={styles.logo} />
              <h1 style={styles.title}>Turn incentives into performance.</h1>
              <p style={styles.subtitle}>Designed for restaurants, retail, and manufacturing teams of all sizes.</p>
              <p className="mb-5" style={styles.savings}>Save 20% with annual billing</p>
            </div>
          </Col>
        </Row>

        <Row className="g-4 mt-2 justify-content-center">
          {plans.map((plan) => (
            <Col key={plan.key} lg={4} md={6}>
              <Card style={{ ...styles.card, ...(plan.featured ? styles.featuredCard : {}) }}>
                <Card.Body style={styles.cardBody}>
                  <div className="d-flex justify-content-between align-items-start mb-1">
                    <h2 style={styles.planTitle}>{plan.name}</h2>
                    {plan.featured ? <p className='desc' bg="warning" text="dark">Most Popular</p> : null}
                  </div>

                  <p style={styles.desc}>{plan.description}</p>

                  <div style={styles.priceLine}>
                    <span style={styles.price}>{plan.price}</span>
                    <span style={styles.period}>{plan.period}</span>
                  </div>
                  <p style={styles.annual}>Save 20% with annual billing</p>

                  <ul style={styles.featureList}>
                    {plan.features.map((feature) => (
                      <li key={feature} style={styles.featureItem}>
                        <span style={styles.bullet} />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>

                  <div style={styles.adminNote}>
                    <strong style={{ color: '#374151' }}>Admin accounts</strong> can manage employees, create incentives, track performance, and control team settings.
                  </div>

                  <div className="d-grid gap-2 mt-3">
                    <Button
                      type="button"
                      disabled={Boolean(checkoutLoading)}
                      onClick={() => startStripeCheckout(plan.key, 'monthly')}
                      style={plan.featured ? styles.btnFeatured : styles.btnPrimary}
                    >
                      {checkoutLoading === `${plan.key}:monthly` ? 'Redirecting…' : `${plan.cta} (Monthly)`}
                    </Button>
                    <Button
                      type="button"
                      disabled={Boolean(checkoutLoading)}
                      onClick={() => startStripeCheckout(plan.key, 'annual')}
                      style={styles.btnSecondary}
                    >
                      {checkoutLoading === `${plan.key}:annual` ? 'Redirecting…' : 'Start Annual (Save 20%)'}
                    </Button>
                  </div>

                </Card.Body>
              </Card>
            </Col>
          ))}
        </Row>

        <div style={styles.panel}>
          <h3 style={styles.panelTitle}>Incentives that pay for themselves</h3>
          <p style={styles.panelText}>Crew Coin helps businesses retain employees, improve performance, and reduce turnover — often covering its own cost within weeks.</p>

          <Row className="g-3">
            <Col md={4}>
              <div style={styles.statCard}>
                <div className="fw-semibold mb-1">📉 Reduce turnover</div>
                <div className="text-muted small">Reward consistency and loyalty to keep your best employees longer.</div>
              </div>
            </Col>
            <Col md={4}>
              <div style={styles.statCard}>
                <div className="fw-semibold mb-1">⚡ Boost performance</div>
                <div className="text-muted small">Incentivize productivity and create friendly competition across your team.</div>
              </div>
            </Col>
            <Col md={4}>
              <div style={styles.statCard}>
                <div className="fw-semibold mb-1">💰 Increase ROI</div>
                <div className="text-muted small">Even small improvements in retention and output can outweigh the cost of the platform.</div>
              </div>
            </Col>
          </Row>

          <p className="text-center text-muted small mt-3 mb-0">Most teams see measurable impact within the first 30 days.</p>
          <p style={styles.testimonial}>“We started using CrewCoin and immediately saw employees working harder and staying more engaged — the incentives actually work.”</p>
        </div>

        <div className="cc-promo-bottom" style={styles.promoBottomWrap}>
          <div className="cc-promo-bottom-label" style={styles.promoBottomLabel}>Special offer</div>
          <div className="cc-promo-bottom-row" style={styles.promoBottomRow}>
            <div className="cc-promo-bottom-segment" style={styles.promoBottomSegment}>
              <p className="cc-promo-bottom-text" style={styles.promoBottomText}>Free Trial</p>
            </div>
            <div className="cc-promo-bottom-segment" style={styles.promoBottomSegment}>
              <span className="cc-promo-bottom-code" style={styles.promoCodePill}>MONTH1FREE</span>
            </div>
            <div className="cc-promo-bottom-actions" style={styles.promoBottomActions}>
              <Button variant="dark" size="sm" style={styles.promoBottomBtn} onClick={copyPromoCode}>
                {copied ? 'Copied!' : 'Copy code'}
              </Button>
            </div>
          </div>
          <p style={{ fontSize: '.86rem', color: '#6b7280', textAlign: 'center', margin: '8px 0 0' }}>
            Cancel anytime before renewal. No charge today.
          </p>
        </div>
      </Container>

      {showPromoModal ? (
        <div
          className="cc-promo-overlay"
          style={styles.promoOverlay}
          role="dialog"
          aria-modal="true"
          aria-labelledby="promo-title"
          onClick={() => setShowPromoModal(false)}
        >
          <div className="cc-promo-modal-card" style={styles.promoModalCard} onClick={(e) => e.stopPropagation()}>
            <div style={styles.promoModalHeader}>
              <h3 id="promo-title" className="cc-promo-modal-title" style={styles.promoModalTitle}>Free Trial Offer</h3>
              <button
                type="button"
                onClick={() => setShowPromoModal(false)}
                aria-label="Close"
                style={{ border: 'none', background: 'transparent', fontSize: '1.4rem', lineHeight: 1, color: '#6b7280', padding: 0 }}
              >
                ×
              </button>
            </div>
            <div className="cc-promo-modal-body" style={styles.promoModalBody}>
              <p className="mb-2">Use this coupon at checkout to get free trial:</p>
              <div style={{ ...styles.promoCodePill, margin: '0 auto 12px', display: 'table', fontSize: '1.2rem', padding: '10px 16px' }}>MONTH1FREE</div>
              <p className="text-muted mb-0" style={{ fontSize: '.95rem', textAlign: 'center' }}>
                Applies to monthly plans only.
              </p>
              <p className="text-muted mb-0" style={{ fontSize: '.9rem', textAlign: 'center', marginTop: 8 }}>
                Cancel anytime before renewal. No charge today.
              </p>
            </div>
            <div style={styles.promoModalFooter}>
              <Button variant="outline-secondary" onClick={copyPromoCode}>{copied ? 'Copied!' : 'Copy code'}</Button>
              <Button variant="warning" onClick={closePromoModal}>Got it</Button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}



